import { Autocomplete, Tab, Tabs, TextField, Tooltip } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import ClientDropdown from "components/Dropdowns/ClientDropdown";
import ViewClientProfile from "components/Clients/ViewClientProfile";
import InputField from "components/Input/InputField";
import { API_URL, insurance_companies, insurance_cover_type } from "const/constants";
import useKothar from "context/useKothar";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getThemedTabsSx } from "theme/tabsTheme";
import EntryDocumentsPanel from "components/ModuleDocuments/EntryDocumentsPanel";
import SendNotificationModal from "../Accounts/SendNotificationModal";
import { usePreselectClientFromLocation } from "../shared/moduleClients/usePreselectClientFromLocation";
import ModuleLockedTab from "../shared/ModuleLockedTab";
import ModulePaymentsTab from "components/ModulePayments/ModulePaymentsTab";
import ModuleTasksTab from "components/Tasks/ModuleTasksTab";
import InsuranceStatusHistoryTab from "./AddInsurance/InsuranceStatusHistoryTab";
import { defaultGstSetting } from "utils/gstCalc";
import GstChargeField from "components/GstChargeField";
import { useInsuranceStatusHistory } from "./insuranceStatusHistoryApi";
import { promptReferralShare } from "../Clients/ReferralSharePrompt";

const TAB_DETAILS = 0;
const TAB_DOCUMENTS = 1;
const TAB_STATUS = 2;
const TAB_PAYMENTS = 3;
const TAB_TASKS = 4;

const coverTypeFilter = createFilterOptions({
  stringify: (option) => option?.label || option?.value || "",
});

const INSURANCE_TYPE_OPTIONS = [
  { label: "OSHC", value: "OSHC" },
  { label: "OVHC", value: "OVHC" },
];

const today = () => format(new Date(), "yyyy-MM-dd");

const INITIAL_STATE = {
  clientId: null,
  name: null,
  number: null,
  passportNumber: null,
  passportExpiry: "",
  visaStatus: "",
  visaExpiry: "",
  caseOfficer: "",
  reference: "",
  insuranceCompany: "",
  startingDate: today(),
  endDate: today(),
  paymentType: "",
  payBy: "",
  cost: "",
  serviceCharge: "",
  extraCharges: "",
  kotharCost: "",
  discount: "",
  costGst: defaultGstSetting(false),
  serviceChargeGst: defaultGstSetting(true),
  extraChargesGst: defaultGstSetting(false),
  kotharCostGst: defaultGstSetting(false),
  type: "",
  coverType: "",
  spouseName: "",
  spouseAddress: "",
  spousePhoneNumber: "",
  children: 0,
  familyMembers: "",
  documents: [],
  importantNote: "",
};

const normalizeInsuranceDocuments = (item = {}) => {
  if (Array.isArray(item.documents) && item.documents.length > 0) {
    return item.documents;
  }
  return [];
};

const SaveFirstNotice = () => (
  <div className="rounded-xl border border-dashed border-app-border bg-app-surface-muted/40 px-4 py-8 text-center">
    <p className="text-sm text-app-text-muted m-0">
      Save the insurance record first to start tracking status updates and payments.
    </p>
  </div>
);

const FieldLabel = ({ children, required = false, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[11px] font-semibold uppercase tracking-wide text-app-text-secondary mb-1.5"
  >
    {children}
    {required ? <span className="text-red-500"> *</span> : null}
  </label>
);

const FormSection = ({ step, title, hint, children }) => (
  <section className="rounded-xl border border-app-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 py-3 border-b border-app-border bg-app-surface-muted/40">
      {step ? (
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
          {step}
        </span>
      ) : null}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-app-text m-0">{title}</h3>
        {hint ? <p className="text-xs text-app-text-muted mt-0.5 mb-0">{hint}</p> : null}
      </div>
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const formGrid = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4";
const btnSecondary =
  "inline-flex items-center justify-center rounded-lg border border-app-border bg-app-surface px-4 py-2 text-sm font-medium text-app-text hover:bg-app-surface-muted transition-colors disabled:opacity-50";
const btnPrimary =
  "inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50";

const TAB_QUERY_PARAM_TO_INDEX = {
  details: TAB_DETAILS,
  documents: TAB_DOCUMENTS,
  status: TAB_STATUS,
  payments: TAB_PAYMENTS,
  tasks: TAB_TASKS,
};

const AddInsurance = () => {
  const [data, setData] = useState(INITIAL_STATE);
  const [childNames, setChildNames] = useState([]);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    () => TAB_QUERY_PARAM_TO_INDEX[searchParams.get("tab")] ?? TAB_DETAILS
  );
  const [notifyOpen, setNotifyOpen] = useState(false);

  const { id: routeId } = useParams();
  const [{ usersList, clientList }, { refetchUsers, refetchClient }] = useKothar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state } = useLocation();

  useEffect(() => {
    if (!usersList?.length) refetchUsers();
  }, [usersList?.length, refetchUsers]);

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);

  usePreselectClientFromLocation({ setData, editId: routeId || state?.item?.id });

  const clientId = data?.clientId ?? data?.id;
  const recordId = data?.id || routeId;

  const { data: statusHistory = [] } = useInsuranceStatusHistory(recordId, Boolean(recordId));

  // Guards against a later refetch (window focus, cache invalidation) overwriting edits in progress.
  const hydratedFromServerRef = useRef(false);

  const { data: fetchedInsurance } = useQuery(
    ["insuranceDetail", routeId],
    async () => {
      const res = await axios.get(`${API_URL}/insurance/${routeId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data?.data ?? null;
    },
    { enabled: !!routeId, staleTime: 0, cacheTime: 0, refetchOnWindowFocus: false }
  );

  // Server payload first (form draft hydrates once); the tab badge shows only when > 0.
  const openInsuranceTaskCount = Number(
    fetchedInsurance?.openTaskCount ?? data?.openTaskCount ?? 0
  );

  // A row click passes the list's snapshot of the record through navigation state, which survives a
  // browser reload. Left as the only source it goes stale the moment the record is saved, so it is a
  // first-paint placeholder only — the server's copy always loads and wins.
  useEffect(() => {
    const item = state?.item;
    if (!item || hydratedFromServerRef.current) return;
    setData({
      ...INITIAL_STATE,
      ...item,
      documents: normalizeInsuranceDocuments(item),
    });
    setChildNames(Array.isArray(item?.child) ? item.child : []);
  }, [state]);

  useEffect(() => {
    if (!fetchedInsurance || hydratedFromServerRef.current) return;
    hydratedFromServerRef.current = true;
    setData({
      ...INITIAL_STATE,
      ...fetchedInsurance,
      documents: normalizeInsuranceDocuments(fetchedInsurance),
    });
    setChildNames(Array.isArray(fetchedInsurance?.child) ? fetchedInsurance.child : []);
  }, [fetchedInsurance]);

  useEffect(() => {
    const count = Number(data.children) || 0;
    setChildNames((prev) => {
      const next = [...prev];
      while (next.length < count) next.push("");
      while (next.length > count) next.pop();
      return next;
    });
  }, [data.children]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const coverType = data?.coverType?.trim?.() || data?.coverType || "";
  const showSpouseFields = coverType === "Couple" || coverType === "Family";
  const showChildrenFields = coverType === "Family" || coverType === "Single Parent";
  const showFamilyMembersField = coverType === "Family";

  const coverTypeOptions = useMemo(() => {
    const map = new Map();
    insurance_cover_type.forEach((item) => map.set(item.value.toLowerCase(), item));
    if (coverType && !map.has(coverType.toLowerCase())) {
      map.set(coverType.toLowerCase(), { label: coverType, value: coverType });
    }
    return Array.from(map.values());
  }, [coverType]);

  const exitAfterSaveRef = useRef(false);
  const tabBeforeSaveRef = useRef(TAB_DETAILS);

  const { mutate, isLoading: saving } = useMutation(postData, {
    onSuccess(saved) {
      const justCreated = Boolean(saved?.id && !recordId);
      toast.success(justCreated ? "Insurance created" : "Insurance updated");

      if (justCreated) {
        setData((prev) => ({ ...prev, ...saved, id: saved.id }));
        promptReferralShare({
          module: "insurance",
          entryId: saved.id,
          clientId: saved?.clientId ?? clientId,
        });
        navigate(`/admin/insurance/edit/${saved.id}`, { replace: true });
      }

      queryClient.invalidateQueries(["insurance-clients-page"]);
      const savedClientId = saved?.clientId ?? clientId;
      if (savedClientId != null) {
        queryClient.invalidateQueries(["insurance-client-records", String(savedClientId)]);
      }

      if (exitAfterSaveRef.current) {
        navigate("/admin/insurance");
        return;
      }

      if (justCreated) {
        setActiveTab(Math.min(tabBeforeSaveRef.current + 1, TAB_PAYMENTS));
      }
    },
    onError() {
      toast.error(recordId ? "Error updating insurance" : "Error saving insurance");
    },
  });

  async function postData(payload) {
    if (recordId) {
      const res = await axios.put(`${API_URL}/insurance/${payload?.id}`, payload);
      return res?.data?.data ?? null;
    }
    const res = await axios.post(`${API_URL}/insurance`, payload);
    return res?.data?.data ?? null;
  }

  const handleSubmit = (e, exitAfter = false) => {
    e.preventDefault();
    if (!clientId) {
      toast.error("Please select a client");
      setActiveTab(TAB_DETAILS);
      return;
    }
    exitAfterSaveRef.current = exitAfter;
    tabBeforeSaveRef.current = activeTab;
    mutate({
      ...data,
      clientId,
      firstName: data?.name,
      child: childNames,
      cost: Number(data?.cost) || 0,
    });
  };

  const notifyContext = useMemo(
    () => ({ clientId, caseOfficer: data?.caseOfficer, module: "insurance" }),
    [clientId, data?.caseOfficer]
  );

  const defaultNotifyMessage = useMemo(() => {
    const clientName = data?.name || "Client";
    const parts = [
      `Insurance payment update for ${clientName}.`,
      data?.insuranceCompany ? `Company: ${data.insuranceCompany}.` : null,
    ].filter(Boolean);
    return parts.join(" ");
  }, [data?.name, data?.insuranceCompany]);

  const currentWorkflowStatus = statusHistory?.[0]?.toStatus || data?.status;

  const hasCoverMembersSection = showFamilyMembersField || showSpouseFields || showChildrenFields;
  const steps = {
    client: 1,
    policy: 2,
    coverMembers: hasCoverMembersSection ? 3 : null,
    period: hasCoverMembersSection ? 4 : 3,
    payment: hasCoverMembersSection ? 5 : 4,
    charges: hasCoverMembersSection ? 6 : 5,
    notes: hasCoverMembersSection ? 7 : 6,
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <SendNotificationModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        users={usersList || []}
        context={{ ...notifyContext, defaultMessage: defaultNotifyMessage }}
      />

      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t px-4 py-4 border-b border-app-border">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-app-border text-app-text hover:bg-app-surface-muted transition-colors"
              >
                <IoArrowBack className="text-lg" />
              </button>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-bold text-app-text m-0">
                    {recordId ? "Edit insurance" : "Add insurance"}
                  </h1>
                  {data?.migrated ? (
                    <Tooltip title="Migrated from legacy data — complete the missing details">
                      <span className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                        Migrated
                      </span>
                    </Tooltip>
                  ) : null}
                </div>
                <p className="text-xs text-app-text-muted mt-0.5 mb-0">
                  Documents, status history, and payments
                  {clientId ? (
                    <>
                      {" · "}
                      <ViewClientProfile
                        clientId={clientId}
                        clientName={data?.name}
                        variant="link"
                      />
                    </>
                  ) : null}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {currentWorkflowStatus ? (
                  <span className="inline-flex rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary">
                    {currentWorkflowStatus}
                  </span>
                ) : null}
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => setActiveTab(TAB_STATUS)}
                >
                  Update status
                </button>
              </div>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={getThemedTabsSx()}
          >
            <Tab label="Details" />
            <ModuleLockedTab
              label="Documents"
              locked={!recordId}
              lockMessage="Create the insurance record first to unlock this tab"
            />
            <ModuleLockedTab
              label="Status history"
              locked={!recordId}
              lockMessage="Create the insurance record first to unlock this tab"
            />
            <ModuleLockedTab
              label="Payments"
              locked={!recordId}
              lockMessage="Create the insurance record first to unlock this tab"
            />
            <ModuleLockedTab
              label={openInsuranceTaskCount > 0 ? `Tasks (${openInsuranceTaskCount})` : "Tasks"}
              locked={!recordId}
              lockMessage="Create the insurance record first to unlock this tab"
            />
          </Tabs>

          <form onSubmit={(e) => handleSubmit(e, false)} className="px-4 py-6">
            {activeTab === TAB_DETAILS && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormSection step={steps.client} title="Client" hint="Who is this policy for?">
                  <div className="flex flex-col items-start gap-3">
                    <ClientDropdown
                      data={data}
                      setData={setData}
                      disabled={Boolean(recordId)}
                      hideEditButton
                    />

                    <ViewClientProfile
                      clientId={clientId}
                      clientName={data?.name}
                      variant="link"
                      displayName="View profile"
                    />
                  </div>
                </FormSection>

                <FormSection
                  step={steps.policy}
                  title="Policy"
                  hint="Company, product type, and level of cover."
                >
                  <div className={formGrid}>
                    <div>
                      <FieldLabel required>Insurance company</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={insurance_companies}
                        value={
                          insurance_companies.find(
                            (item) => item.value === data?.insuranceCompany
                          ) || null
                        }
                        onChange={(_, value) =>
                          setData((prev) => ({ ...prev, insuranceCompany: value?.value || "" }))
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select company" required />
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel required>Insurance type</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={INSURANCE_TYPE_OPTIONS}
                        value={
                          INSURANCE_TYPE_OPTIONS.find((item) => item.value === data?.type) || null
                        }
                        onChange={(_, value) =>
                          setData((prev) => ({ ...prev, type: value?.value || "" }))
                        }
                        getOptionLabel={(option) => option?.label || ""}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="OSHC or OVHC" required />
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel required>Cover type</FieldLabel>
                      <Autocomplete
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        openOnFocus
                        size="small"
                        options={coverTypeOptions}
                        inputValue={data?.coverType || ""}
                        onInputChange={(_, newInputValue, reason) => {
                          if (reason === "reset") return;
                          setData((prev) => ({ ...prev, coverType: newInputValue }));
                        }}
                        filterOptions={(options, params) => {
                          const filtered = coverTypeFilter(options, params);
                          const inputValue = params.inputValue.trim();
                          const isExisting = options.some(
                            (option) =>
                              (option.label || "").toLowerCase() === inputValue.toLowerCase() ||
                              (option.value || "").toLowerCase() === inputValue.toLowerCase()
                          );
                          if (inputValue !== "" && !isExisting) {
                            filtered.push({
                              inputValue,
                              label: `Add "${inputValue}"`,
                              value: inputValue,
                            });
                          }
                          return filtered;
                        }}
                        value={
                          coverTypeOptions.find((item) => item.value === data?.coverType) || null
                        }
                        onChange={(_, newValue) => {
                          if (typeof newValue === "string") {
                            setData((prev) => ({ ...prev, coverType: newValue.trim() }));
                          } else if (newValue?.inputValue) {
                            setData((prev) => ({
                              ...prev,
                              coverType: newValue.inputValue.trim(),
                            }));
                          } else {
                            setData((prev) => ({ ...prev, coverType: newValue?.value || "" }));
                          }
                        }}
                        getOptionLabel={(option) => {
                          if (typeof option === "string") return option;
                          if (option?.inputValue) return option.inputValue;
                          return option?.label || option?.value || "";
                        }}
                        renderOption={(props, option) => {
                          const { key, ...optionProps } = props;
                          return (
                            <li key={key} {...optionProps}>
                              {option.inputValue ? (
                                <span className="font-semibold text-primary">
                                  Add &ldquo;{option.inputValue}&rdquo;
                                </span>
                              ) : (
                                option.label
                              )}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Single, Couple, Family…" required />
                        )}
                      />
                    </div>
                  </div>
                </FormSection>

                {hasCoverMembersSection && (
                  <FormSection
                    step={steps.coverMembers}
                    title="Cover members"
                    hint="Details for people included on this policy."
                  >
                    <div className="space-y-4">
                      {showFamilyMembersField && (
                        <div className="max-w-xs">
                          <InputField
                            label="Number of family members"
                            name="familyMembers"
                            type="number"
                            required
                            value={data?.familyMembers ?? ""}
                            onChange={handleInputChange}
                          />
                        </div>
                      )}
                      {showSpouseFields && (
                        <div className={formGrid}>
                          <InputField
                            label="Spouse name"
                            name="spouseName"
                            required={coverType === "Couple"}
                            value={data?.spouseName || ""}
                            onChange={handleInputChange}
                          />
                          <InputField
                            label="Spouse phone"
                            name="spousePhoneNumber"
                            required={coverType === "Couple"}
                            value={data?.spousePhoneNumber || ""}
                            onChange={handleInputChange}
                          />
                          <InputField
                            label="Spouse address"
                            name="spouseAddress"
                            required={coverType === "Couple"}
                            value={data?.spouseAddress || ""}
                            onChange={handleInputChange}
                            divClassName="sm:col-span-2 lg:col-span-3"
                          />
                        </div>
                      )}
                      {showChildrenFields && (
                        <div className="space-y-3">
                          <div className="max-w-xs">
                            <InputField
                              label="Number of children"
                              name="children"
                              type="number"
                              required
                              value={data?.children ?? ""}
                              onChange={handleInputChange}
                            />
                          </div>
                          {childNames.length > 0 && (
                            <div className={formGrid}>
                              {childNames.map((childName, index) => (
                                <InputField
                                  key={index}
                                  label={`Child ${index + 1} name`}
                                  value={childName || ""}
                                  onChange={(e) =>
                                    setChildNames((prev) => [
                                      ...prev.slice(0, index),
                                      e.target.value,
                                      ...prev.slice(index + 1),
                                    ])
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </FormSection>
                )}

                <FormSection step={steps.period} title="Policy period">
                  <div className={`${formGrid} max-w-2xl`}>
                    <InputField
                      label="Start date"
                      name="startingDate"
                      type="date"
                      required
                      value={data?.startingDate || ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="End date"
                      name="endDate"
                      type="date"
                      required
                      value={data?.endDate || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </FormSection>

                <FormSection step={steps.payment} title="Payment" hint="How this policy is paid.">
                  <div className={formGrid}>
                    <InputField
                      label="Payment type"
                      name="paymentType"
                      required
                      value={data?.paymentType || ""}
                      onChange={handleInputChange}
                    />
                    <InputField
                      label="Paid by"
                      name="payBy"
                      required
                      value={data?.payBy || ""}
                      onChange={handleInputChange}
                      placeholder="Client or agent"
                    />{" "}
                    <GstChargeField
                      label="Insurance price"
                      amountName="cost"
                      amountValue={data?.cost}
                      onAmountChange={handleInputChange}
                      gst={data?.costGst}
                      onGstChange={(next) => setData((prev) => ({ ...prev, costGst: next }))}
                    />
                  </div>
                </FormSection>

                <FormSection
                  step={steps.notes}
                  title="Notes"
                  hint="Internal only — not shown to the client."
                >
                  <textarea
                    rows={4}
                    name="importantNote"
                    value={data?.importantNote || ""}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text placeholder:text-app-text-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Anything the team should know about this policy…"
                  />
                </FormSection>
              </div>
            )}

            {activeTab === TAB_DOCUMENTS && (
              <div className="max-w-5xl">
                <EntryDocumentsPanel
                  moduleType="INSURANCE"
                  entryId={recordId}
                  clientId={clientId}
                  documents={data.documents || []}
                  onDocumentsChange={(documents) => setData((prev) => ({ ...prev, documents }))}
                />
              </div>
            )}

            {activeTab === TAB_STATUS &&
              (recordId ? (
                <InsuranceStatusHistoryTab insuranceId={recordId} />
              ) : (
                <SaveFirstNotice />
              ))}

            {activeTab === TAB_PAYMENTS &&
              (recordId ? (
                <ModulePaymentsTab
                  moduleType="INSURANCE"
                  moduleId={recordId}
                  clientId={data.clientId}
                  charges={{
                    primaryCharge: data.cost,
                    serviceCharge: data.serviceCharge,
                    extraCharges: data.extraCharges,
                    primaryChargeGst: data.costGst,
                    serviceChargeGst: data.serviceChargeGst,
                    extraChargesGst: data.extraChargesGst,
                    discount: data.discount,
                  }}
                  onPaymentSubmitted={() => setNotifyOpen(true)}
                />
              ) : (
                <SaveFirstNotice />
              ))}

            {activeTab === TAB_TASKS &&
              (recordId ? (
                <ModuleTasksTab
                  entryType="INSURANCE"
                  entryId={recordId}
                  clientId={clientId}
                  clientName={data?.name}
                  taskModule="Insurance"
                  assignTitle={`Insurance: ${data?.insuranceCompany || "Insurance policy"} (${
                    data?.name || "client"
                  })`}
                  onTasksChanged={() =>
                    queryClient.invalidateQueries({ queryKey: ["insuranceDetail", routeId] })
                  }
                />
              ) : (
                <SaveFirstNotice />
              ))}

            <div className="flex flex-wrap justify-end gap-2 mt-6 pt-6 border-t border-app-border">
              <button type="button" className={btnSecondary} onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={(e) => handleSubmit(e, true)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & exit"}
              </button>
              <button type="submit" className={btnPrimary} disabled={saving}>
                {saving ? "Saving…" : recordId ? "Update insurance" : "Create insurance"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddInsurance;
